/**
 * Header patient search — "Recently viewed".
 *
 * Tapping the empty header search lists the last patients opened on THIS device, so the
 * front desk re-opens the patient in the chair (to take their photo, say) without typing.
 * Recorded by Header.vue watching the route, so a profile opened from an Open Dental program
 * link, a family link, a task or the search itself all count.
 *
 * ⛔ PATNUMS ONLY. localStorage bypasses the demo-mask axios interceptor (CLAUDE.md → Demo
 * mask, rule 4). This list stores `{ p: PatNum, t: epochMs }` and nothing else; names, phones
 * and DOBs are resolved at render time from the roster, which is already masked. That is why
 * this key is deliberately NOT in PHI_CACHE_KEYS — there is nothing in it to mask. Never add a
 * patient field here.
 *
 * Per site (switching site never shows another practice's patients), newest first, capped at
 * RECENT_PATIENTS_MAX. Cleared on logout (store/customer.js). Every storage access is guarded:
 * private mode or a full quota must not break the header.
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE — cypress/node-tests/recent-patients.test.js executes it.
 */

export const RECENT_PATIENTS_KEY = 'teamio_recent_patients';
export const RECENT_PATIENTS_MAX = 8;

// Same shape PatientProfile.vue validates: SiteUUID-PatNum.
const PROFILE_ID = /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})-(\d+)$/;

function storage() {
  try {
    if (typeof window === 'undefined' || !window) return null;
    return window.localStorage || null;
  } catch (e) {
    return null;
  }
}

function toPatNum(value) {
  let n = NaN;
  if (typeof value === 'number') n = value;
  else if (typeof value === 'string' && value.trim() !== '') n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
}

function readAll(ls) {
  try {
    const raw = ls.getItem(RECENT_PATIENTS_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (e) {
    return {};
  }
}

function sanitize(list) {
  if (!Array.isArray(list)) return [];
  const out = [];
  list.forEach((entry) => {
    const p = toPatNum(entry && entry.p);
    if (p !== null) out.push({ p, t: Number(entry.t) || 0 });
  });
  return out;
}

/** PatNums opened on this device for `siteID`, newest first. */
export function getRecentPatNums(siteID) {
  if (!siteID) return [];
  const ls = storage();
  if (!ls) return [];
  return sanitize(readAll(ls)[siteID]).map((e) => e.p);
}

/**
 * Move `patNum` to the front of `siteID`'s list and return the new list. An invalid PatNum
 * is ignored (the current list comes back unchanged).
 */
export function recordRecentPatient(siteID, patNum, now) {
  if (!siteID) return [];
  const ls = storage();
  if (!ls) return [];

  const all = readAll(ls);
  const previous = sanitize(all[siteID]);
  const p = toPatNum(patNum);
  if (p === null) return previous.map((e) => e.p);

  const t = typeof now === 'number' && isFinite(now) ? now : Date.now();
  const next = [{ p, t }].concat(previous.filter((e) => e.p !== p)).slice(0, RECENT_PATIENTS_MAX);
  all[siteID] = next;
  try {
    ls.setItem(RECENT_PATIENTS_KEY, JSON.stringify(all));
  } catch (e) {
    // Private mode / full quota: the visit still shows for this page load.
  }
  return next.map((e) => e.p);
}

export function clearRecentPatients() {
  const ls = storage();
  if (!ls) return;
  try {
    ls.removeItem(RECENT_PATIENTS_KEY);
  } catch (e) {
    // Nothing to clear.
  }
}

/** `{ siteID, patNum }` from a /app/patientprofile/:patientID param, or null. */
export function parsePatientProfileID(patientID) {
  if (typeof patientID !== 'string') return null;
  const m = patientID.match(PROFILE_ID);
  if (!m) return null;
  const patNum = toPatNum(m[2]);
  return patNum === null ? null : { siteID: m[1], patNum };
}
